import axios from 'axios';

const bettingAPI = axios.create({
    baseURL: '/api/betting',
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 10000
});

bettingAPI.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('📡 Betting API Request:', config.method.toUpperCase(), config.url, config.data);
        return config;
    },
    (error) => {
        console.error('❌ Betting API Request Error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for better error handling
bettingAPI.interceptors.response.use(
    (response) => {
        console.log('✅ Betting API Response:', response.status, response.data);
        return response;
    },
    (error) => {
        if (error.response) {
            console.error('❌ Betting API Error Response:', {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers
            });
            
            const customError = new Error(error.response.data?.message || 'Server error');
            customError.response = error.response;
            customError.status = error.response.status;
            return Promise.reject(customError);
        } else if (error.request) {
            console.error('❌ Betting API No Response:', error.request);
            const customError = new Error('No response from server. Please check your connection.');
            customError.request = error.request;
            return Promise.reject(customError);
        } else {
            console.error('❌ Betting API Error:', error.message);
            return Promise.reject(error);
        }
    }
);

export const bettingService = {
    // Place a bet
    placeBet: async (betData) => {
        try {
            const response = await bettingAPI.post('/place-bet', betData);
            
            if (response.data && response.data.success) {
                if (response.data.data) {
                    response.data.data.potentialWin = parseFloat(response.data.data.potentialWin) || 0;
                    response.data.data.exposureAmount = parseFloat(response.data.data.exposureAmount) || 0;
                    response.data.data.availableBalance = parseFloat(response.data.data.availableBalance) || 0;
                    response.data.data.stake = parseFloat(response.data.data.stake) || 0;
                    response.data.data.actualOdds = parseFloat(response.data.data.actualOdds) || 0;
                    response.data.data.userOdds = parseFloat(response.data.data.userOdds) || 0;
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('Error in placeBet:', error);
            throw error;
        }
    },

    

    // Add these methods to your bettingService

// Get market position for bookmaker
getMarketPosition: async (marketId) => {
    try {
        const response = await bettingAPI.get(`/market-position/${marketId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching market position:', error);
        throw error;
    }
},

// Get bookmaker bets for a specific market
getMarketBets: async (marketId, type = 'all') => {
    try {
        const response = await bettingAPI.get(`/market-bets/${marketId}`, { params: { type } });
        return response.data;
    } catch (error) {
        console.error('Error fetching market bets:', error);
        throw error;
    }
},

    // Get user's exposure
    getUserExposure: async () => {
        try {
            const response = await bettingAPI.get('/user/exposure');
            return response.data;
        } catch (error) {
            console.error('Error in getUserExposure:', error);
            throw error;
        }
    },

    // Get user's bets (general)
    getUserBets: async (params = {}) => {
        try {
            const response = await bettingAPI.get('/user/bets', { params });
            return response.data;
        } catch (error) {
            console.error('Error in getUserBets:', error);
            throw error;
        }
    },

    // Get current active bets
    getCurrentBets: async (params) => {
        try {
            const response = await bettingAPI.get('/user/current-bets', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching current bets:', error);
            throw error;
        }
    },

    // Get complete bet history (settled + unsettled)
    getBetHistory: async (params = {}) => {
        try {
            const response = await bettingAPI.get('/user/bet-history', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching bet history:', error);
            throw error;
        }
    },

    // Get settled bets only (for statement)
    getSettledBets: async (params = {}) => {
        try {
            const response = await bettingAPI.get('/user/settled-bets', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching settled bets:', error);
            throw error;
        }
    },

    // Get user's session bets for a market
getUserSessionBets: async (marketId) => {
    try {
        const response = await bettingAPI.get(`/user/session-bets/${marketId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching session bets:', error);
        throw error;
    }
},

    // Get deposit/withdrawal transactions
    getTransactionHistory: async (params = {}) => {
        try {
            const response = await bettingAPI.get('/user/transactions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching transaction history:', error);
            throw error;
        }
    },

    // Get complete account statement (combined bets + transactions)
    getAccountStatement: async (params = {}) => {
        try {
            const response = await bettingAPI.get('/user/account-statement', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching account statement:', error);
            throw error;
        }
    },

    // Get specific bet details
    getBetDetails: async (betId) => {
        try {
            const response = await bettingAPI.get(`/bet/${betId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching bet details:', error);
            throw error;
        }
    },

    // Cancel a bet
    cancelBet: async (betId) => {
        try {
            const response = await bettingAPI.post(`/bet/${betId}/cancel`);
            return response.data;
        } catch (error) {
            console.error('Error in cancelBet:', error);
            throw error;
        }
    }
};

export default bettingService;